import { useTranslation } from 'react-i18next';

export default function TradingHoursSection() {
  const { t, i18n } = useTranslation();

  const tradingSchedule = [
    { dayKey: 'markets.days.monday', hoursKey: 'markets.hours.monThu', status: 'active' },
    { dayKey: 'markets.days.tuesday', hoursKey: 'markets.hours.monThu', status: 'active' },
    { dayKey: 'markets.days.wednesday', hoursKey: 'markets.hours.monThu', status: 'active' },
    { dayKey: 'markets.days.thursday', hoursKey: 'markets.hours.monThu', status: 'active' },
    { dayKey: 'markets.days.friday', hoursKey: 'markets.hours.friday', status: 'active' },
    { dayKey: 'markets.days.saturday', hoursKey: 'markets.hours.closed', status: 'closed' },
    { dayKey: 'markets.days.sunday', hoursKey: 'markets.hours.closed', status: 'closed' },
  ];

  const holidays = [
    {
      dateKey: 'markets.holidays.newYear.date',
      eventKey: 'markets.holidays.newYear.event',
      status: 'closed',
    },
    {
      dateKey: 'markets.holidays.christmas.date',
      eventKey: 'markets.holidays.christmas.event',
      status: 'closed',
    },
    {
      dateKey: 'markets.holidays.independence.date',
      eventKey: 'markets.holidays.independence.event',
      status: 'early-close',
    },
    {
      dateKey: 'markets.holidays.thanksgiving.date',
      eventKey: 'markets.holidays.thanksgiving.event',
      status: 'early-close',
    },
  ];

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
            {t('markets.tho')}
          </h2>
          <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
            {t('markets.moh')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-6 md:p-8 border border-gray-200">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-calendar-line text-blue-600 mr-3"></i>
              {t('markets.wts')}
            </h3>

            <div className="space-y-3">
              {tradingSchedule.map((schedule, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-300 ${
                    schedule.status === 'active'
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-center">
                    <div
                      className={`w-3 h-3 rounded-full mr-3 ${
                        schedule.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                      }`}
                    ></div>
                    <span className="font-semibold text-gray-900">
                      {t(schedule.dayKey)}
                    </span>
                  </div>
                  <span
                    className={`font-mono text-sm ${
                      schedule.status === 'active' ? 'text-green-700' : 'text-red-700'
                    }`}
                  >
                    {t(schedule.hoursKey)}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-start">
                <i className="ri-information-line text-blue-600 mr-2 mt-1"></i>
                <div className="text-sm text-blue-800">
                  <strong>{t('markets.note')}:</strong> {t('markets.atad')}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-6 md:p-8 border border-gray-200">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-calendar-event-line text-orange-600 mr-3"></i>
              {t('markets.hs')}
            </h3>

            <div className="space-y-3">
              {holidays.map((holiday, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-300 ${
                    holiday.status === 'closed'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div>
                    <div className="font-semibold text-gray-900">{t(holiday.eventKey)}</div>
                    <div className="text-sm text-gray-600">{t(holiday.dateKey)}</div>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      holiday.status === 'closed'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {holiday.status === 'closed'
                      ? t('markets.mclo')
                      : t('markets.eclo')}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-orange-50 rounded-xl border border-orange-200">
              <div className="flex items-start">
                <i className="ri-alert-line text-orange-600 mr-2 mt-1"></i>
                <div className="text-sm text-orange-800">
                  <strong>{t('markets.hn')}:</strong> {t('markets.thm')}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 md:mt-12 bg-gradient-to-r from-green-50 to-emerald-50 rounded-3xl p-6 md:p-8 border border-green-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-500 rounded-full animate-pulse mr-4"></div>
              <div>
                <h4 className="text-lg md:text-xl font-bold text-gray-900">{t('markets.cms')}</h4>
                <p className="text-green-700">{t('markets.mac')}</p>
              </div>
            </div>
            <div className="text-left md:text-right">
              <div className="text-sm text-gray-600">{t('markets.st')}</div>
              <div className="text-xl md:text-2xl font-mono font-bold text-gray-900">
                {new Date().toLocaleTimeString(i18n.language === 'zh' ? 'zh-HK' : 'en-US', {
                  timeZone: 'Asia/Shanghai',
                  hour12: false,
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}